/**
 * Hook useSharedWebSocket
 *
 * Gestisce una SINGOLA connessione WebSocket condivisa tra:
 * - Sistema foto (photo_auth, photo_request, ecc.)
 * - Sistema chat DM (dm_auth, dm_message, ecc.)
 *
 * Questo evita conflitti quando entrambi i sistemi tentano di aprire
 * connessioni WebSocket sullo stesso endpoint /ws/chat
 */

import { useState, useEffect, useCallback } from 'react';

// ==================== TYPES ====================

export interface SharedWebSocketConfig {
  userId: string;
  userName: string;
  userType: 'desktop' | 'mobile';
  enabled?: boolean;
  appVersion?: string;
}

export type WebSocketMessageHandler = (data: any) => void;

interface SharedWebSocketState {
  isConnected: boolean;
  isConnecting: boolean;
  photoAuthenticated: boolean;
  dmAuthenticated: boolean;
}

// ==================== SINGLETON INSTANCE ====================

// Singleton per gestire la connessione condivisa
let sharedWsInstance: WebSocket | null = null;
let sharedWsState: SharedWebSocketState = {
  isConnected: false,
  isConnecting: false,
  photoAuthenticated: false,
  dmAuthenticated: false
};

// Map di handlers registrati per tipo di messaggio
const messageHandlers = new Map<string, Set<WebSocketMessageHandler>>();

// Config corrente
let currentConfig: SharedWebSocketConfig | null = null;

// Reconnect state
let reconnectTimeout: NodeJS.Timeout | null = null;
let reconnectAttempts = 0;
const MAX_RECONNECT_ATTEMPTS = 5;

// State change listeners
const stateListeners = new Set<(state: SharedWebSocketState) => void>();

// Page Visibility tracking
let visibilityListenerAdded = false;

// ==================== INTERNAL FUNCTIONS ====================

function notifyStateChange() {
  stateListeners.forEach(listener => listener({ ...sharedWsState }));
}

function updateState(updates: Partial<SharedWebSocketState>) {
  sharedWsState = { ...sharedWsState, ...updates };
  notifyStateChange();
}

function getWebSocketUrl(): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = window.location.host;
  return `${protocol}//${host}/ws/chat`;
}

/**
 * Gestisce il ritorno della pagina in foreground
 * Verifica se il WebSocket è ancora connesso e riconnette se necessario
 */
function handleVisibilityChange() {
  if (document.visibilityState === 'visible') {
    console.log('[SharedWS] 👁️ Page became visible, checking connection...');

    // Piccolo delay per dare tempo al browser di "svegliarsi"
    setTimeout(() => {
      // Verifica se dovremmo essere connessi ma non lo siamo
      if (currentConfig?.enabled && currentConfig?.userId) {
        const wsState = sharedWsInstance?.readyState;

        if (wsState !== WebSocket.OPEN && wsState !== WebSocket.CONNECTING) {
          console.log('[SharedWS] 🔄 Connection lost while in background, reconnecting...');
          reconnectAttempts = 0; // Reset attempts for fresh reconnect
          connectShared();
        } else if (wsState === WebSocket.OPEN) {
          // Connessione OK, invia ping per verificare che sia ancora attiva
          console.log('[SharedWS] ✅ Connection still active, sending ping...');
          const pingSent = sendSharedMessage({ type: 'ping' });

          // Se il ping fallisce, forza riconnessione
          if (!pingSent) {
            console.log('[SharedWS] ⚠️ Ping failed, forcing reconnect...');
            reconnectAttempts = 0;
            sharedWsInstance = null;
            updateState({ isConnected: false, photoAuthenticated: false, dmAuthenticated: false });
            connectShared();
          }
        }
      }
    }, 500); // 500ms delay per stabilizzare
  } else {
    console.log('[SharedWS] 🌙 Page went to background');
  }
}

/**
 * Gestisce il focus della finestra (backup per mobile)
 */
function handleWindowFocus() {
  // Solo se la pagina non è già visibile (evita doppio trigger)
  if (document.visibilityState !== 'visible') return;

  console.log('[SharedWS] 🎯 Window focused');

  // Verifica connessione se siamo tornati da background
  if (currentConfig?.enabled && currentConfig?.userId) {
    const wsState = sharedWsInstance?.readyState;

    if (wsState !== WebSocket.OPEN && wsState !== WebSocket.CONNECTING) {
      console.log('[SharedWS] 🔄 Connection lost, reconnecting on focus...');
      reconnectAttempts = 0;
      connectShared();
    }
  }
}

/**
 * Inizializza i listener per Page Visibility API e window focus
 * Chiamato una volta sola quando si attiva il primo WebSocket
 */
function setupVisibilityListener() {
  if (visibilityListenerAdded) return;

  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('focus', handleWindowFocus);
  visibilityListenerAdded = true;
  console.log('[SharedWS] 👁️ Visibility and focus listeners added');
}

// Deduplicazione: il backend invia lo stesso messaggio via photo + DM channel
// causando doppia ricezione sullo stesso WebSocket
const recentMessageKeys = new Set<string>();

function dispatchMessage(data: any) {
  const type = data.type;

  // Dedup: skip messaggi duplicati (stesso type+timestamp ricevuti dalla stessa connessione)
  if (data.timestamp) {
    const dedupKey = `${type}:${data.timestamp}`;
    if (recentMessageKeys.has(dedupKey)) {
      return; // Duplicato, skip silenziosamente
    }
    recentMessageKeys.add(dedupKey);
    setTimeout(() => recentMessageKeys.delete(dedupKey), 5000);
  }

  // DEBUG: Log incoming messages (escluso pong per pulizia)
  if (type !== 'pong') {
    console.log('[SharedWS] 📨 Message received:', type, data);
  }

  const handlers = messageHandlers.get(type);

  // DEBUG: Log handler status per photo e turbine events
  if (type === 'photo_uploaded' || type === 'request_completed_by_mobile' || type.startsWith('turbine:')) {
    console.log(`[SharedWS] 🔍 Handlers for "${type}":`, handlers ? handlers.size : 0);
  }

  if (handlers) {
    handlers.forEach(handler => {
      try {
        handler(data);
      } catch (err) {
        console.error(`[SharedWS] Error in handler for ${type}:`, err);
      }
    });
  } else if (type !== 'pong' && type !== 'available_mobiles') {
    // 'pong' e 'available_mobiles' sono messaggi di sistema che non richiedono handler
    console.log(`[SharedWS] ⚠️ No handlers registered for "${type}"`);
  }

  // Dispatch to wildcard handlers (receive all messages)
  const wildcardHandlers = messageHandlers.get('*');
  if (wildcardHandlers) {
    wildcardHandlers.forEach(handler => {
      try {
        handler(data);
      } catch (err) {
        console.error('[SharedWS] Error in wildcard handler:', err);
      }
    });
  }
}

function handleAuthSuccess(data: any) {
  if (data.type === 'photo_auth_success') {
    console.log('[SharedWS] Photo auth success');
    updateState({ photoAuthenticated: true });
  } else if (data.type === 'dm_auth_success') {
    console.log('[SharedWS] DM auth success');
    updateState({ dmAuthenticated: true });
  }
}

function connectShared() {
  if (!currentConfig || !currentConfig.enabled) {
    return;
  }

  // ✅ Non connettersi se userId è 'unknown' o vuoto
  const isValidUserId = currentConfig.userId && currentConfig.userId !== 'unknown' && currentConfig.userId !== '';
  if (!isValidUserId) {
    console.log('[SharedWS] ❌ userId non valido, skip connessione:', currentConfig.userId);
    return;
  }

  // Setup visibility listener (una volta sola)
  setupVisibilityListener();

  if (sharedWsInstance?.readyState === WebSocket.OPEN) {
    console.log('[SharedWS] Already connected');
    return;
  }

  if (sharedWsState.isConnecting) {
    console.log('[SharedWS] Connection in progress');
    return;
  }

  updateState({ isConnecting: true });

  try {
    const wsUrl = getWebSocketUrl();
    console.log('[SharedWS] Connecting to:', wsUrl);

    const ws = new WebSocket(wsUrl);
    sharedWsInstance = ws;

    ws.onopen = () => {
      console.log('[SharedWS] Connected');
      reconnectAttempts = 0;
      updateState({ isConnected: true, isConnecting: false });

      // Send BOTH auth messages (photo + dm) on the same connection

      // 1. Photo auth
      const photoAuth = {
        type: 'photo_auth',
        userId: String(currentConfig!.userId),
        userName: currentConfig!.userName,
        clientType: currentConfig!.userType,
        deviceInfo: {
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          language: navigator.language
        },
        appVersion: currentConfig!.appVersion || '2.0.95'
      };
      console.log('[SharedWS] Sending photo_auth');
      ws.send(JSON.stringify(photoAuth));

      // 2. DM auth
      const dmAuth = {
        type: 'dm_auth',
        userId: currentConfig!.userId,
        userName: currentConfig!.userName,
        userType: currentConfig!.userType
      };
      console.log('[SharedWS] Sending dm_auth');
      ws.send(JSON.stringify(dmAuth));
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        // Handle auth success internally
        handleAuthSuccess(data);

        // Dispatch to registered handlers
        dispatchMessage(data);
      } catch (err) {
        console.error('[SharedWS] Error parsing message:', err);
      }
    };

    ws.onclose = (event) => {
      console.log('[SharedWS] Closed:', event.code, event.reason);
      // Se questo socket è già stato rimpiazzato (ensureSharedConnected/riconnessione),
      // non azzerare il riferimento al NUOVO socket né schedulare un'altra riconnessione.
      if (sharedWsInstance !== ws) return;
      sharedWsInstance = null;
      updateState({
        isConnected: false,
        isConnecting: false,
        photoAuthenticated: false,
        dmAuthenticated: false
      });

      // Auto-reconnect
      if (currentConfig?.enabled && reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 30000);
        console.log(`[SharedWS] Reconnecting in ${delay}ms (attempt ${reconnectAttempts + 1})`);

        reconnectTimeout = setTimeout(() => {
          reconnectAttempts++;
          connectShared();
        }, delay);
      }
    };

    ws.onerror = (error) => {
      console.error('[SharedWS] Error:', error);
    };

  } catch (err) {
    console.error('[SharedWS] Connection error:', err);
    updateState({ isConnecting: false });
  }
}

function disconnectShared() {
  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = null;
  }

  reconnectAttempts = MAX_RECONNECT_ATTEMPTS; // Prevent auto-reconnect

  if (sharedWsInstance) {
    sharedWsInstance.close(1000, 'User disconnect');
    sharedWsInstance = null;
  }

  updateState({
    isConnected: false,
    isConnecting: false,
    photoAuthenticated: false,
    dmAuthenticated: false
  });
}

/**
 * Chiude il WebSocket condiviso e riapre la possibilità di riconnettersi.
 * Usato al logout: dopo aver mandato photo_logout/dm_logout, il backend
 * cancella il client dalle sue mappe; se non chiudiamo il WS, alla
 * prossima auth il client vede già il WS aperto e non rimanda mai
 * gli auth → il mobile non torna più disponibile lato desktop.
 */
export function resetSharedWebSocket() {
  if (sharedWsInstance) {
    sharedWsInstance.close(1000, 'Reset on logout');
    sharedWsInstance = null;
  }
  reconnectAttempts = 0; // permetti riconnessione al prossimo enable
  updateState({
    isConnected: false,
    isConnecting: false,
    photoAuthenticated: false,
    dmAuthenticated: false
  });
}

// ==================== PUBLIC API ====================

/**
 * Forza una riconnessione immediata se il WS condiviso non è aperto.
 * Usato dai flussi critici (es. scanner remoto) quando un send fallisce:
 * azzera i tentativi (il backoff/max-attempts può aver rinunciato) e riconnette.
 */
export function ensureSharedConnected(): void {
  if (sharedWsInstance?.readyState === WebSocket.OPEN) return;
  if (sharedWsState.isConnecting) return;
  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = null;
  }
  reconnectAttempts = 0;
  connectShared();
}

/**
 * Registra un handler per un tipo specifico di messaggio
 * @param type - Tipo messaggio (es. 'dm_new_message', 'photo_request', '*' per tutti)
 * @param handler - Funzione handler
 * @returns Funzione per rimuovere l'handler
 */
export function registerMessageHandler(type: string, handler: WebSocketMessageHandler): () => void {
  if (!messageHandlers.has(type)) {
    messageHandlers.set(type, new Set());
  }
  messageHandlers.get(type)!.add(handler);

  return () => {
    messageHandlers.get(type)?.delete(handler);
  };
}

/**
 * Invia un messaggio sul WebSocket condiviso
 */
export function sendSharedMessage(message: any): boolean {
  if (sharedWsInstance?.readyState === WebSocket.OPEN) {
    sharedWsInstance.send(JSON.stringify(message));
    return true;
  }
  return false;
}

/**
 * Verifica se il WebSocket è connesso
 */
export function isSharedWsConnected(): boolean {
  return sharedWsState.isConnected;
}

/**
 * Ottiene lo stato corrente
 */
export function getSharedWsState(): SharedWebSocketState {
  return { ...sharedWsState };
}

// ==================== REACT HOOK ====================

/**
 * Hook React per usare il WebSocket condiviso
 *
 * @example
 * ```tsx
 * const { isConnected, send, registerHandler } = useSharedWebSocket({
 *   userId: '123',
 *   userName: 'Mario',
 *   userType: 'mobile',
 *   enabled: true
 * });
 *
 * useEffect(() => {
 *   // Registra handler per messaggi DM
 *   return registerHandler('dm_new_message', (data) => {
 *     console.log('Nuovo messaggio:', data);
 *   });
 * }, [registerHandler]);
 * ```
 */
export function useSharedWebSocket(config: SharedWebSocketConfig) {
  const [state, setState] = useState<SharedWebSocketState>(sharedWsState);

  // Sync config
  useEffect(() => {
    currentConfig = config;

    // ✅ Non connettersi se userId è 'unknown' o vuoto
    const isValidUserId = config.userId && config.userId !== 'unknown' && config.userId !== '';
    if (config.enabled && isValidUserId) {
      connectShared();
    } else if (config.enabled && !isValidUserId) {
      console.log('[SharedWS] ⏳ Attendo userId valido (attuale:', config.userId, ')');
    }

    // Subscribe to state changes
    const listener = (newState: SharedWebSocketState) => {
      setState(newState);
    };
    stateListeners.add(listener);

    return () => {
      stateListeners.delete(listener);
      // Don't disconnect on unmount - let other components use the connection
    };
  }, [config.userId, config.userName, config.userType, config.enabled]);

  // Ping periodico
  useEffect(() => {
    if (!state.isConnected) return;

    const pingInterval = setInterval(() => {
      sendSharedMessage({ type: 'ping' });
    }, 25000);

    return () => clearInterval(pingInterval);
  }, [state.isConnected]);

  const send = useCallback((message: any): boolean => {
    return sendSharedMessage(message);
  }, []);

  const registerHandler = useCallback((type: string, handler: WebSocketMessageHandler) => {
    return registerMessageHandler(type, handler);
  }, []);

  const disconnect = useCallback(() => {
    disconnectShared();
  }, []);

  const reconnect = useCallback(() => {
    reconnectAttempts = 0;
    connectShared();
  }, []);

  return {
    // State
    isConnected: state.isConnected,
    isConnecting: state.isConnecting,
    photoAuthenticated: state.photoAuthenticated,
    dmAuthenticated: state.dmAuthenticated,

    // Actions
    send,
    registerHandler,
    disconnect,
    reconnect
  };
}

export default useSharedWebSocket;
